import { Store } from '@tanstack/react-store';
import type { SortingState } from '@tanstack/react-table';
import { createManagementActions } from './createManagementStore';

export interface HubspotAutomatedReviewSummary {
  status:
    | 'Queued'
    | 'Running'
    | 'Passed'
    | 'Failed'
    | 'Error'
    | 'Canceled'
    | 'Stale';
  effectiveStatus:
    | 'Queued'
    | 'Running'
    | 'Passed'
    | 'Failed'
    | 'Error'
    | 'Canceled'
    | 'Stale';
  passedCount: number;
  failedCount: number;
  warningCount: number;
  skippedCount: number;
  totalCount: number;
  effectivePassedCount: number;
  effectiveFailedCount: number;
  effectiveWarningCount: number;
  effectiveSkippedCount: number;
  effectiveTotalCount: number;
  lastCompletedAt: Date | null;
  latestRunId: string | null;
  latestReviewInputHash: string | null;
  latestPolicyVersion: string | null;
  hasOverrides: boolean;
}

export interface HubspotTicketDetail {
  id: string;
  hubspotId: string;
  subject: string | null;
  content: string | null;
  hsPipeline: string | null;
  hsPipelineStage: string | null;
  hsTicketPriority: string | null;
  mappedSubmissionStatus: string | null;
  requestUniqueId: string | null;
  dappCollectionAccountAddress: string | null;
  dappReleaseAccountAddress: string | null;
  compliesWithPolicies: boolean | null;
  requestorAuthorized: boolean | null;
  closedDate: Date | null;
  createdAt: Date;
  lastModifiedAt: Date;
  requestType: string | null;
  releaseId: string | null;
  dappId: string | null;
  dappName: string | null;
  dappPackageName: string | null;
  dappStatus: string | null;
  hasPublishedReleaseHistory: boolean | null;
  releaseVersion: string | null;
  apkFileSize: number | null;
  submissionCount: number;
  submissionSource: 'Portal' | 'CLI';
  tokenAppName: string | null;
  tokenVersionName: string | null;
  tokenVersionCode: number | null;
  tokenIconUrl: string | null;
  automatedReviewSummary: HubspotAutomatedReviewSummary | null;
  assignedUserId: string | null;
  assignedUserName: string | null;
}

interface HubspotManagementState {
  tickets: HubspotTicketDetail[];
  pipelineStages: string[];
  filters: {
    pipeline: string;
    requestType: string;
    source: string;
    assignee: string;
    search: string;
  };
  sorting: SortingState;
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
  };
  ui: {
    isLoading: boolean;
    selectedTicketId: string | null;
    pendingTicketSelection: 'first' | 'last' | null;
  };
}

const initialFilters: HubspotManagementState['filters'] = {
  pipeline: '',
  requestType: '',
  source: '',
  assignee: '',
  search: '',
};

const defaultSorting: SortingState = [{ id: 'createdAt', desc: true }];

export const HUBSPOT_TICKETS_PAGE_SIZE = 20;

export const hubspotManagementStore = new Store<HubspotManagementState>({
  tickets: [],
  pipelineStages: [],
  filters: initialFilters,
  sorting: defaultSorting,
  pagination: { currentPage: 1, totalPages: 1, total: 0 },
  ui: {
    isLoading: true,
    selectedTicketId: null,
    pendingTicketSelection: null,
  },
});

const actions = createManagementActions(hubspotManagementStore, {
  initialFilters,
  defaultSorting,
});

export const {
  updateFilter,
  setSorting,
  setPage,
  setPaginationInfo,
  setLoading,
} = actions;

export const clearFilters = () => {
  hubspotManagementStore.setState((prev) => ({
    ...prev,
    filters: initialFilters,
    pagination: { ...prev.pagination, currentPage: 1 },
  }));
};

export const setTickets = (tickets: HubspotTicketDetail[]) => {
  hubspotManagementStore.setState((prev) => ({ ...prev, tickets }));
};

export const setPipelineStages = (stages: string[]) => {
  hubspotManagementStore.setState((prev) => ({
    ...prev,
    pipelineStages: stages,
  }));
};

export const selectTicket = (ticketId: string | null) => {
  actions.setUiField('selectedTicketId', ticketId);
};

export const setPendingSelection = (selection: 'first' | 'last' | null) => {
  actions.setUiField('pendingTicketSelection', selection);
};

export const hydrate = (urlParams: {
  page?: number;
  pipeline?: string;
  requestType?: string;
  source?: string;
  assignee?: string;
  search?: string;
  sortColumn?: string;
  sortDirection?: string;
  ticketId?: string | null;
}) => {
  const sorting: SortingState =
    urlParams.sortColumn && urlParams.sortDirection
      ? [
          {
            id: urlParams.sortColumn,
            desc: urlParams.sortDirection === 'desc',
          },
        ]
      : [{ id: 'createdAt', desc: true }];

  hubspotManagementStore.setState((prev) => ({
    ...prev,
    filters: {
      pipeline: urlParams.pipeline || '',
      requestType: urlParams.requestType || '',
      source: urlParams.source || '',
      assignee: urlParams.assignee || '',
      search: urlParams.search || '',
    },
    sorting,
    pagination: {
      ...prev.pagination,
      currentPage: urlParams.page && urlParams.page > 0 ? urlParams.page : 1,
    },
    ui: {
      ...prev.ui,
      selectedTicketId: urlParams.ticketId || null,
    },
  }));
};
